/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react';
import { Button, DatePicker, Form, Input, Select, Space, Typography } from 'antd';
import type { FormInstance } from 'antd';
import commonStyles from '../../../../../styles/commonStyles';
import type { Field } from '../../../../../models/Field';
import type { Plot } from '../../../../../models/Plot';
import type { Bed } from '../../../../../models/Bed';
import { normalizeSelectOptions } from '../utils';
import { formStyles } from '../constants';
import type { PlantingFormModalValues } from '../types';

interface LocationsListSectionProps {
  form: FormInstance<PlantingFormModalValues>;
  fields: Field[];
  fieldsLoading: boolean;
  plotsByField: Record<string, Plot[]>;
  bedsByPlot: Record<string, Bed[]>;
  plotsLoading: boolean;
  bedsLoading: boolean;
  isEditMode: boolean;
  allowAddLocations: boolean;
  locationsListRules: any[];
  onFieldChange: (formIndex: number, fieldId: number | string | undefined) => void;
  onPlotChange: (formIndex: number, plotId: number | string | undefined) => void;
  onBedChange: (bedId: string | undefined) => void;
}

/** Dynamic Form.List for adding/removing planting locations with cascade selects */
const LocationsListSection: React.FC<LocationsListSectionProps> = ({
  form,
  fields,
  fieldsLoading,
  plotsByField,
  bedsByPlot,
  plotsLoading,
  bedsLoading,
  isEditMode,
  allowAddLocations,
  locationsListRules,
  onFieldChange,
  onPlotChange,
  onBedChange,
}) => {
  const fieldOptions = useMemo(() => normalizeSelectOptions<Field>(fields), [fields]);

  return (
    <>
      <Typography.Title level={5} css={formStyles.sectionTitle}>
        Локации
      </Typography.Title>

      <Form.List name="locations" rules={locationsListRules}>
        {(fieldsList, { add, remove }, { errors }) => (
          <>
            {fieldsList.map((fieldItem, index) => {
              const fieldId = form.getFieldValue(['locations', fieldItem.name, 'fieldId']);
              const plotId = form.getFieldValue(['locations', fieldItem.name, 'plotId']);
              const plotOptions = normalizeSelectOptions<Plot>(plotsByField[String(fieldId)] || []);
              const bedOptions = normalizeSelectOptions<Bed>(bedsByPlot[String(plotId)] || []);

              return (
                <Space
                  key={fieldItem.key}
                  direction="vertical"
                  css={formStyles.locationContainer}
                >
                  <Space css={formStyles.locationHeader} align="start">
                    <Typography.Text strong>Локация {index + 1}</Typography.Text>
                    {fieldsList.length > 1 && allowAddLocations && (
                      <Button type="link" danger onClick={() => remove(fieldItem.name)}>
                        Удалить
                      </Button>
                    )}
                  </Space>

                  <Form.Item name={[fieldItem.name, 'id']} hidden>
                    <Input type="hidden" />
                  </Form.Item>

                  <Form.Item
                    label="Поле"
                    name={[fieldItem.name, 'fieldId']}
                    rules={[{ required: true, message: 'Выберите поле' }]}
                  >
                    <Select
                      placeholder="Выберите поле"
                      loading={fieldsLoading || plotsLoading}
                      options={fieldOptions}
                      onChange={(value) => onFieldChange(fieldItem.name, value)}
                      allowClear={allowAddLocations}
                      disabled={isEditMode}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Участок"
                    name={[fieldItem.name, 'plotId']}
                    rules={[{ required: true, message: 'Выберите участок' }]}
                  >
                    <Select
                      placeholder={fieldId ? 'Выберите участок поля' : 'Сначала выберите поле'}
                      disabled={isEditMode || !fieldId}
                      loading={plotsLoading}
                      options={plotOptions}
                      onChange={(value) => onPlotChange(fieldItem.name, value)}
                      allowClear={allowAddLocations}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Грядка"
                    name={[fieldItem.name, 'bedId']}
                    rules={[{ required: true, message: 'Выберите грядку' }]}
                  >
                    <Select
                      placeholder={plotId ? 'Выберите грядку' : 'Сначала выберите участок'}
                      disabled={isEditMode || !plotId}
                      loading={bedsLoading}
                      options={bedOptions}
                      showSearch
                      optionFilterProp="label"
                      onChange={(bedId) => onBedChange(bedId ? String(bedId) : undefined)}
                    />
                  </Form.Item>

                  <Space direction="horizontal" css={formStyles.fullWidthSpace}>
                    <Form.Item
                      label="Дата начала"
                      name={[fieldItem.name, 'fromDate']}
                      rules={[{ required: true, message: 'Укажите дату начала' }]}
                      css={formStyles.flexOne}
                    >
                      <DatePicker format="YYYY-MM-DD" css={commonStyles.fullWidth} />
                    </Form.Item>
                    <Form.Item
                      label="Дата окончания"
                      name={[fieldItem.name, 'toDate']}
                      css={formStyles.flexOne}
                    >
                      <DatePicker format="YYYY-MM-DD" css={commonStyles.fullWidth} allowClear />
                    </Form.Item>
                  </Space>
                </Space>
              );
            })}

            <Form.ErrorList errors={errors} />

            {allowAddLocations && (
              <Button type="dashed" onClick={() => add()} block>
                Добавить локацию
              </Button>
            )}
          </>
        )}
      </Form.List>
    </>
  );
};

export default LocationsListSection;
